// The email draft card's one-click send, apart from its markup so the
// contract can be tested without mounting the card.
import { useState } from 'react';
import { actionRequest } from '@/components/assistant-ui/action-api';
import type { ActionView } from '@/components/assistant-ui/action-confirmation';
import type { useActionConfirmation } from '@/components/assistant-ui/use-action-confirmation';
import { draftFields, type EmailDraftFields } from './email-model';

type Request = typeof actionRequest;
type ActionState = ReturnType<typeof useActionConfirmation>;

// Staged while the connection was set to Read: the card never grants write
// access, so Send first restages to read the connection's current access.
export const needsWriteAccess = (action: ActionView) =>
  action.decision.connection?.needsWriteAccess === true;

const sameFields = (left: EmailDraftFields, right: EmailDraftFields) =>
  JSON.stringify(left) === JSON.stringify(right);

export function draftArgs(action: ActionView, fields: EmailDraftFields) {
  return {
    ...action.args,
    to: fields.to,
    cc: fields.cc.length ? fields.cc : undefined,
    bcc: fields.bcc.length ? fields.bcc : undefined,
    subject: fields.subject,
    body: fields.body,
  };
}

/**
 * Inside one trusted click: restage edited fields (a new approval digest),
 * present that exact version and approve it. A draft staged while the
 * connection was set to Read restages too; if writing is still not allowed
 * it comes back unapproved, for the card to say how to allow it.
 */
export async function submitDraft(
  action: ActionView,
  fields: EmailDraftFields,
  cardToken: string | null,
  request: Request = actionRequest,
): Promise<{ action: ActionView; approved: boolean }> {
  let current = action;
  let token = cardToken;
  if (!sameFields(fields, draftFields(action.args)) || needsWriteAccess(action)) {
    ({ action: current } = await request<{ action: ActionView }>(action.id, '/restage', 'POST', {
      digest: action.digest,
      args: draftArgs(action, fields),
    }));
    if (needsWriteAccess(current)) return { action: current, approved: false };
    ({ cardToken: token } = await request<{ cardToken: string }>(current.id, '/presented', 'POST', {
      digest: current.digest,
    }));
  }
  if (!token) throw Object.assign(new Error('action_card_token_required'), { status: 409 });
  const { action: done } = await request<{ action: ActionView }>(current.id, '/approve', 'POST', {
    digest: current.digest,
    cardToken: token,
  });
  return { action: done, approved: true };
}

// Refusals that mean this version is out of date with the connection: the
// account or its send tool changed, or writing was turned off after staging.
const RESTAGE_ON = ['action_changed_review_again', 'connection_write_not_enabled'];

/**
 * After a failed click: read the current draft (the original id follows its
 * restage chain). If the connection changed since this version was made,
 * restage the same fields so the card shows the current version.
 */
export async function recoverDraft(
  action: ActionView,
  error: unknown,
  request: Request = actionRequest,
): Promise<ActionView> {
  let { action: latest } = await request<{ action: ActionView }>(action.id);
  if (RESTAGE_ON.includes((error as Error).message) && latest.status === 'pending')
    ({ action: latest } = await request<{ action: ActionView }>(latest.id, '/restage', 'POST', {
      digest: latest.digest,
      args: latest.args,
    }));
  return latest;
}

export function useDraftSend(state: ActionState) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [writeNeeded, setWriteNeeded] = useState(false);
  const send = async (action: ActionView, fields: EmailDraftFields) => {
    setBusy(true);
    setNotice(null);
    setWriteNeeded(false);
    try {
      const result = await submitDraft(action, fields, state.cardToken());
      state.adopt(result.action);
      setWriteNeeded(!result.approved && needsWriteAccess(result.action));
    } catch (error) {
      const status = (error as { status?: number }).status;
      setNotice(status === 409 ? state.copy.tryAgain : state.copy.toolError);
      try {
        const latest = await recoverDraft(action, error);
        state.adopt(latest);
        // Writing was turned off after this draft was staged: say so, as for
        // a draft staged while the connection was set to Read.
        if (needsWriteAccess(latest)) {
          setNotice(null);
          setWriteNeeded(true);
        }
      } catch {
        /* Fail closed: the card stays as it was. */
      }
    } finally {
      setBusy(false);
    }
  };
  return { busy, notice, writeNeeded, send };
}
